import { useEffect, useState } from 'react';

/**
 * Debounces a reactive value. Returns a delayed version of the input that only
 * updates after the specified delay has elapsed without changes. Triggers a
 * re-render when the debounced value settles.
 *
 * Use this when you want to react to a stabilized value in a useEffect.
 * For debouncing imperative function calls, use useDebounceCallback instead.
 *
 * @example
 * const [search, setSearch] = useState('');
 * const debouncedSearch = useDebounce(search, 500);
 *
 * useEffect(() => {
 *   fetchResults(debouncedSearch);
 * }, [debouncedSearch]);
 */
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
